import React, { useEffect, useState } from "react";
import { Button, makeStyles, Typography } from "@material-ui/core";
import AlertDialog from "../../../../../common/AlertDialog";
import { useIntl } from "react-intl";
import { formatCategoriesList } from "../../../../../../utils/utils";
import { getDiseases, getSymptoms } from "../../../../../../services/commonservices";
import { DISEASES_PAGE_SIZE, SYMPTOMS_PAGE_SIZE } from "../../../../../../utils/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  changeDiseasesAction,
  changeDiseasesPageSizeAction,
  changeSearchTextAction,
  changeSymptomsAction,
  changeSymptomsPageSizeAction,
  changeTotalDiseasesAction,
  changeTotalSymptomsAction,
} from "../../../../../../reducers/searchOmimState";
import Grid from "@material-ui/core/Grid";
import SelectedDiseasesComponent from "./components/SelectedDiseasesComponent";
import DiseasesTableComponent from "../../../../../common/diseasesTableComponent/DiseasesTableComponent";
import TagListComponent from "../../../../../common/TagListCompoents";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "grid",
    gridTemplateColumns: "1fr .75fr",
    gap: "1rem",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr ",
    },
  },
  actionButtons: {
    display: "flex",
    gap: "1rem",
    flexWrap: "wrap",
    width: "100%",
    justifyContent: "space-between",
  },
  synonymTitle: {
    color: "black",
  },
  modalContent: {
    display: "grid",
    gap: "1rem",
  },
  gridBlock: {
    alignContent: "flex-start",
    display: "grid",
    gap: "1rem",
    [theme.breakpoints.down("sm")]: {
      minHeight: "400px",
    },
  },
  primaryButton: theme.primaryPinkButton,
  secondaryButton: {
    borderRadius: 30,
    border: `2px solid ${theme.palette.selected.main}`,
    color: `${theme.palette.selected.main} !important`,
    padding: theme.spacing(0.5, 3),
    "&:hover": {
      border: `2px solid ${theme.palette.selected.main}`,
      backgroundColor: theme.palette.selected.main,
      color: "white !important",
    },
    "&:disabled": {
      color: theme.palette.disabled.text,
      backgroundColor: theme.palette.disabled.main,
      border: `2px solid ${theme.palette.disabled.main}`,
    },
  },
  dataHeader: {
    display: "flex",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  headerBlock: {
    display: "flex",
    gap: theme.spacing(2),
  },
  headerSubtitle: {
    color: theme.palette.secondary.main,
  },
  firstCap: {
    textTransform: "lowercase !important",
    "&:first-letter": {
      textTransform: "capitalize !important",
    },
  },
}));

const DialogContent = ({ classes, dispatch, intl, currentSection, mobileVersion }) => {
  const newPatient = !!JSON.parse(localStorage.getItem("genome-new-patient"));

  const { patientData, recordId, symptoms } = useSelector((state) => state.patient);
  const { searchText, selectedCategories, diseases } = useSelector((state) => state.searchOmim);

  const handleSearchSuccess = (response) => {
    dispatch(changeSymptomsPageSizeAction(parseInt(response.headers["pagination-page-size"])));
    dispatch(changeTotalSymptomsAction(parseInt(response.headers["pagination-total-results"])));
    dispatch(changeSymptomsAction(response.data));
  };

  const handleSearchOnclick = () => {
    const categories = formatCategoriesList(selectedCategories);
    getSymptoms(searchText, categories, SYMPTOMS_PAGE_SIZE, 0, handleSearchSuccess);
  };

  useEffect(() => {
    if (!!Object.keys(selectedCategories)?.length || !!searchText) {
      handleSearchOnclick();
    } else if (!!diseases?.length) {
      dispatch(changeSymptomsPageSizeAction(null));
      dispatch(changeTotalSymptomsAction(null));
      dispatch(changeSymptomsAction([]));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories]);

  const handleDiseasesSuccess = (response) => {
    dispatch(changeDiseasesAction(response.data));
    dispatch(changeDiseasesPageSizeAction(parseInt(response.headers["pagination-page-size"])));
    dispatch(changeTotalDiseasesAction(parseInt(response.headers["pagination-total-results"])));
  };

  useEffect(() => {
    const formattedSymptoms = symptoms.map((s) => s.symptom_id);

    getDiseases(searchText, formattedSymptoms, recordId, DISEASES_PAGE_SIZE, 0, handleDiseasesSuccess);

    return () => {
      dispatch(changeSearchTextAction(""));
      dispatch(changeDiseasesAction([]));
      dispatch(changeDiseasesPageSizeAction(null));
      dispatch(changeTotalDiseasesAction(null));
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container className={classes.modalContent}>
      <Grid item xs={12}>
        <div className={classes.dataHeader}>
          <div className={classes.headerBlock}>
            <Typography variant={"h6"}>
              {intl.formatMessage({ id: "patient" })} {newPatient ? "" : patientData?.patient_id}
            </Typography>
          </div>
          <div className={classes.headerBlock}>
            <Typography variant={"h6"}>/ {intl.formatMessage({ id: "diseases" })}</Typography>
          </div>
          <div className={classes.headerBlock}>
            <Typography variant={"h6"} className={classes.headerSubtitle}>
              / {intl.formatMessage({ id: "diseasesSelection" })}
            </Typography>
          </div>
        </div>
      </Grid>
      <Grid item xs={12}>
        <Typography variant={"h6"}>{intl.formatMessage({ id: "patient_symptoms" })}</Typography>
        <TagListComponent tagList={symptoms} displayField={"name"} />
      </Grid>
      <div className={classes.container}>
        {(currentSection === 1 || !mobileVersion) && (
          <div className={classes.gridBlock}>
            <DiseasesTableComponent diagnosisScreen checkBackground showScore={true} />
          </div>
        )}
        {(currentSection === 2 || !mobileVersion) && (
          <div className={classes.gridBlock}>
            <SelectedDiseasesComponent />
          </div>
        )}
      </div>
    </Grid>
  );
};

const AddDiseasesDialogComponent = ({ openModal, handleDiseasesModal, handleAddDisease }) => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const mobileVersion = useSelector((state) => state.common.mobileVersion);
  const selectedDiseases = useSelector((state) => state.searchOmim.selectedDiseases);

  const [currentSection, setCurrentSection] = useState(1);
  const handleSectionChange = (direction) => {
    if (direction === "next") {
      setCurrentSection(currentSection + 1);
    } else if (direction === "prev") {
      setCurrentSection(currentSection - 1);
    }
  };

  const buttonCloseText = (
    <Typography className={classes.firstCap}>{intl.formatMessage({ id: "global_buttons_close" })}</Typography>
  );

  const content = (
    <DialogContent
      classes={classes}
      dispatch={dispatch}
      intl={intl}
      currentSection={currentSection}
      mobileVersion={mobileVersion}
    />
  );

  const actionButton = (
    <div className={classes.actionButtons}>
      {mobileVersion && currentSection > 1 && (
        <Button className={`${classes.primaryButton}  ${classes.previous}`} onClick={() => handleSectionChange("prev")}>
          <Typography className={classes.firstCap}>{intl.formatMessage({ id: "previous" })}</Typography>
        </Button>
      )}
      {mobileVersion && currentSection === 1 && (
        <Button
          disabled={!Boolean(Object.keys(selectedDiseases).length !== 0)}
          className={`${classes.primaryButton} ${classes.next}`}
          onClick={() => handleSectionChange("next")}
        >
          <Typography className={classes.firstCap}>{intl.formatMessage({ id: "next" })}</Typography>
        </Button>
      )}
      {(currentSection === 2 || !mobileVersion) && (
        <Button className={classes.primaryButton} onClick={handleAddDisease}>
          <Typography className={classes.firstCap}>{intl.formatMessage({ id: "add_modal_save_button" })}</Typography>
        </Button>
      )}
    </div>
  );

  return (
    <AlertDialog
      title={intl.formatMessage({ id: "addDiseases" })}
      open={openModal}
      buttonCloseText={buttonCloseText}
      description={content}
      onClose={handleDiseasesModal}
      dialogClassName={classes.dialogContent}
      buttonClassName={classes.secondaryButton}
      actionButton={actionButton}
    />
  );
};

export default AddDiseasesDialogComponent;
